/**
 * Pipeline Sharing — Serialize & Deserialize
 *
 * Encodes pipeline state into a compact, lz-string-compressed string
 * suitable for URL hash sharing (`#p=<payload>`).
 *
 * @see docs/design/pipeline-sharing.md
 */

import LZString from 'lz-string';

import { ToolRegistry } from '@/engine/ToolRegistry';
import type { IPipelineStep } from '@/interfaces/IPipeline';
import { generateId } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

const SCHEMA_VERSION = 1;
const HASH_PREFIX = '#p=';

/** Compact representation of a shared pipeline step. */
interface ShareableStep {
  /** toolId */
  t: string;
  /** config (non-default values only) */
  c: Record<string, unknown>;
  /** label (optional) */
  l?: string;
  /** enabled — only present when false */
  e?: 0;
}

/** Top-level shareable pipeline payload. */
interface ShareablePipeline {
  /** Schema version */
  v: number;
  /** Steps */
  s: ShareableStep[];
  /** Pipeline name (optional) */
  n?: string;
  /** Pipeline variables (optional) */
  vars?: Record<string, string>;
  /** Input data (optional, opt-in) */
  i?: string;
}

// ---------------------------------------------------------------------------
// Hydrated result returned by deserialize
// ---------------------------------------------------------------------------

export interface DeserializedPipeline {
  steps: Array<{
    toolId: string;
    config: Record<string, unknown>;
    label?: string;
    isEnabled: boolean;
  }>;
  name?: string;
  variables?: Record<string, string>;
  inputData?: string;
}

/** Step shape accepted by serializePipeline (no runtime `id` required). */
type SerializableStep = {
  toolId: string;
  config: Record<string, unknown>;
  isEnabled: boolean;
  label?: string;
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isNonEmptyObject(val: unknown): val is Record<string, unknown> {
  return typeof val === 'object' && val !== null && !Array.isArray(val) && Object.keys(val).length > 0;
}

function isNonEmptyString(val: unknown): val is string {
  return typeof val === 'string' && val.length > 0;
}

// ---------------------------------------------------------------------------
// Serialize
// ---------------------------------------------------------------------------

/**
 * Serialize pipeline steps into a compressed, URL-safe string.
 *
 * Accepts either:
 * - `serializePipeline(steps, options?)` — array + optional metadata
 * - `serializePipeline({ steps, name?, variables?, inputData? })` — single options object
 *
 * @returns lz-string `compressToEncodedURIComponent` output
 */
export function serializePipeline(
  stepsOrOpts: SerializableStep[] | IPipelineStep[] | {
    steps: SerializableStep[] | IPipelineStep[];
    name?: string;
    variables?: Record<string, string>;
    inputData?: string;
  },
  options?: {
    name?: string;
    variables?: Record<string, string>;
    inputData?: string;
  },
): string {
  let steps: SerializableStep[] | IPipelineStep[];
  let name: string | undefined;
  let variables: Record<string, string> | undefined;
  let inputData: string | undefined;

  if (Array.isArray(stepsOrOpts)) {
    steps = stepsOrOpts;
    name = options?.name;
    variables = options?.variables;
    inputData = options?.inputData;
  } else {
    steps = stepsOrOpts.steps;
    name = stepsOrOpts.name;
    variables = stepsOrOpts.variables;
    inputData = stepsOrOpts.inputData;
  }
  const compactSteps: ShareableStep[] = steps.map((s) => {
    // FR-DIFF-6: Strip user-provided comparison text from diff visualizer
    // configs when input data is not being shared, to prevent leaking content.
    let config = s.config ?? {};
    if (!inputData && config.comparisonInput) {
      const { comparisonInput, compareWith, ...rest } = config as Record<string, unknown>;
      config = rest;
    }

    const cs: ShareableStep = {
      t: s.toolId,
      c: config,
    };
    if (isNonEmptyString(s.label)) {
      cs.l = s.label;
    }
    if (s.isEnabled === false) {
      cs.e = 0;
    }
    return cs;
  });

  const payload: ShareablePipeline = {
    v: SCHEMA_VERSION,
    s: compactSteps,
  };

  if (isNonEmptyString(name)) {
    payload.n = name;
  }
  if (isNonEmptyObject(variables)) {
    payload.vars = variables;
  }
  if (isNonEmptyString(inputData)) {
    payload.i = inputData;
  }

  const json = JSON.stringify(payload);
  return LZString.compressToEncodedURIComponent(json);
}

// ---------------------------------------------------------------------------
// Deserialize
// ---------------------------------------------------------------------------

/**
 * Decompress and validate a shared pipeline payload.
 *
 * - Validates schema version.
 * - Skips unknown toolIds with a console warning.
 * - Generates fresh step IDs on hydration — never trusts IDs from the URL.
 *
 * @returns hydrated pipeline data, or `null` if the payload is structurally invalid.
 */
export function deserializePipeline(compressed: string): DeserializedPipeline | null {
  try {
    const json = LZString.decompressFromEncodedURIComponent(compressed);
    if (!json) return null;

    const payload: unknown = JSON.parse(json);
    if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null;

    const p = payload as Record<string, unknown>;

    // Version check
    if (typeof p.v !== 'number' || p.v < 1) return null;
    if (p.v > SCHEMA_VERSION) {
      console.warn(
        `[pipelineSharing] Shared pipeline uses schema v${p.v}, but this app supports v${SCHEMA_VERSION}. Some features may be missing.`,
      );
    }

    // Steps
    if (!Array.isArray(p.s) || p.s.length === 0) return null;

    const steps: DeserializedPipeline['steps'] = [];
    for (const raw of p.s as unknown[]) {
      if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) continue;
      const r = raw as Record<string, unknown>;
      const toolId = r.t;
      if (typeof toolId !== 'string' || toolId.length === 0) continue;

      // Skip unknown tools — they can't be loaded
      if (!ToolRegistry.has(toolId)) {
        console.warn(`[pipelineSharing] Skipping unknown tool: "${toolId}"`);
        continue;
      }

      steps.push({
        toolId,
        config: (typeof r.c === 'object' && r.c !== null && !Array.isArray(r.c))
          ? (r.c as Record<string, unknown>)
          : {},
        label: typeof r.l === 'string' ? r.l : undefined,
        isEnabled: r.e === 0 ? false : true,
      });
    }

    if (steps.length === 0) return null;

    const result: DeserializedPipeline = { steps };

    if (isNonEmptyString(p.n)) {
      result.name = p.n as string;
    }
    if (isNonEmptyObject(p.vars)) {
      result.variables = p.vars as Record<string, string>;
    }
    if (isNonEmptyString(p.i)) {
      result.inputData = p.i as string;
    }

    return result;
  } catch (err) {
    console.warn('[pipelineSharing] Failed to deserialize shared pipeline:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// URL helpers
// ---------------------------------------------------------------------------

/** Build a full share URL from a compressed pipeline payload. */
export function generateShareUrl(compressed: string): string {
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  return `${origin}/${HASH_PREFIX}${compressed}`;
}

/**
 * Extract the compressed payload from a URL or hash string.
 *
 * Accepts full URLs (`https://.../#p=XYZ`), hash-only strings (`#p=XYZ`),
 * or falls back to `window.location.hash` if no argument is provided.
 *
 * @returns the compressed string, or `null` if not a share URL.
 */
export function parseShareUrl(url?: string): string | null {
  let hash: string;
  if (url !== undefined) {
    const hashIdx = url.indexOf('#');
    hash = hashIdx >= 0 ? url.slice(hashIdx) : '';
  } else {
    hash = typeof window !== 'undefined' ? window.location.hash : '';
  }
  if (!hash.startsWith(HASH_PREFIX)) return null;
  const compressed = hash.slice(HASH_PREFIX.length);
  return compressed.length > 0 ? compressed : null;
}

// ---------------------------------------------------------------------------
// Hydration helper (mirrors loadPipeline pattern)
// ---------------------------------------------------------------------------

/**
 * Convert deserialized steps into full `IPipelineStep[]` with fresh IDs.
 * Steps have already been validated against ToolRegistry during deserialization.
 */
export function hydratePipelineSteps(deserialized: DeserializedPipeline): IPipelineStep[] {
  return deserialized.steps.map((s) => ({
    id: generateId(),
    toolId: s.toolId,
    label: s.label,
    config: s.config ?? {},
    isEnabled: s.isEnabled,
  }));
}
